// T121 (16.10): „Struktura firmy” — drzewko kto komu podlega (admin). Zmiana przełożonego: przeciągnij osobę na inną
// albo wybierz z listy przy osobie. Zapis przez PUT /users/:id { supervisorId } — backend pilnuje pętli i Biura bez przełożonego.
import React, { useState, useEffect, useCallback } from 'react';
import { RoleBadge, Avatar, fullName, useToast } from '../components/UI';
import api from '../utils/api';

const ROLE_ORDER = { ADMIN: 0, COORDINATOR: 1, ADMINISTRATION: 2, WAREHOUSE: 3, EVENT: 4 };
const byName = (a, b) => (ROLE_ORDER[a.role] - ROLE_ORDER[b.role]) || `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`, 'pl');
const canApprove = (u) => u.role === 'ADMIN' || u.canApproveHours || u.canApproveLeaves;

export default function StructurePage() {
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState('');
  const [closed, setClosed] = useState({});     // zwinięte gałęzie
  const [drag, setDrag] = useState(null);       // przeciągana osoba
  const [over, setOver] = useState(null);       // nad kim upuszczamy
  const [showEvent, setShowEvent] = useState(false);
  const { show, ToastComponent } = useToast();

  const load = useCallback(() => api.getUsers().then(setUsers).catch(e => show(e.message, 'error')), []); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  if (!users) return <p style={{ color: '#94A3B8', padding: 20 }}>Ładowanie…</p>;

  const byId = Object.fromEntries(users.map(u => [u.id, u]));
  const kids = {};
  for (const u of users) if (u.supervisorId && byId[u.supervisorId]) (kids[u.supervisorId] = kids[u.supervisorId] || []).push(u);
  Object.values(kids).forEach(l => l.sort(byName));
  const roots = users.filter(u => !u.supervisorId || !byId[u.supervisorId]).sort(byName);
  // Eventowi bez przełożonego — osobno (godziny zatwierdza opiekun eventowych), żeby nie zalali drzewka
  const eventLoose = roots.filter(u => u.role === 'EVENT');
  const mainRoots = roots.filter(u => u.role !== 'EVENT');
  const total = (id) => (kids[id] || []).reduce((n, k) => n + 1 + total(k.id), 0);
  const isAncestor = (anc, id) => { let c = byId[id]?.supervisorId; for (let i = 0; c && i < 100; i++) { if (c === anc) return true; c = byId[c]?.supervisorId; } return false; };
  const match = (u) => q && fullName(u).toLowerCase().includes(q.toLowerCase());
  const subtreeMatches = (u) => match(u) || (kids[u.id] || []).some(subtreeMatches);

  const move = async (person, supId) => {
    if (supId === person.supervisorId) return;
    if (supId && (supId === person.id || isAncestor(person.id, supId))) { show('Nie można — powstałaby pętla (ktoś przełożonym własnego przełożonego).', 'error'); return; }
    const target = supId ? fullName(byId[supId]) : 'bez przełożonego';
    if (!window.confirm(`${fullName(person)} → ${supId ? `pod: ${target}` : target}?`)) return;
    try { await api.updateUser(person.id, { supervisorId: supId || null }); show('Przełożony zmieniony'); load(); }
    catch (e) { show(e.message, 'error'); }
  };

  const warnings = (u) => {
    const w = [];
    if (u.role === 'ADMINISTRATION' && !byId[u.supervisorId]) w.push(['Biuro bez przełożonego', '#DC2626']);
    else if (u.role !== 'ADMIN' && u.role !== 'EVENT' && !byId[u.supervisorId]) w.push(['bez przełożonego — godziny i urlopy zatwierdza tylko admin', '#B45309']);
    if ((kids[u.id] || []).length && !canApprove(u)) w.push(['ma podwładnych, ale nie może nic zatwierdzać', '#B45309']);
    return w;
  };

  // Funkcja, nie komponent — komponent zdefiniowany w środku byłby montowany od nowa przy każdym renderze (psuje przeciąganie)
  const renderNode = (u, depth) => {
    const ch = kids[u.id] || [];
    const isClosed = q ? !subtreeMatches(u) : closed[u.id];
    const dropOk = drag && drag.id !== u.id && !isAncestor(drag.id, u.id) && drag.supervisorId !== u.id;
    return (
      <div key={u.id} style={{ marginLeft: depth ? 22 : 0, borderLeft: depth ? '2px solid #E2E8F0' : 'none', paddingLeft: depth ? 8 : 0 }}>
        <div draggable={u.role !== 'ADMIN'}
          onDragStart={() => setDrag(u)} onDragEnd={() => { setDrag(null); setOver(null); }}
          onDragOver={e => { if (dropOk) { e.preventDefault(); setOver(u.id); } }}
          onDragLeave={() => setOver(o => (o === u.id ? null : o))}
          onDrop={e => { e.preventDefault(); const d = drag; setDrag(null); setOver(null); if (dropOk) move(d, u.id); }}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 6px', margin: '2px 0', borderRadius: 6, flexWrap: 'wrap',
            background: over === u.id ? '#DBEAFE' : match(u) ? '#FEF9C3' : 'transparent', cursor: u.role !== 'ADMIN' ? 'grab' : 'default',
            outline: over === u.id ? '2px dashed #2563EB' : 'none' }}>
          <button className="btn btn-ghost btn-sm" style={{ padding: '0 4px', visibility: ch.length ? 'visible' : 'hidden', fontSize: 11 }}
            onClick={() => setClosed(c => ({ ...c, [u.id]: !c[u.id] }))}>{isClosed ? '▸' : '▾'}</button>
          <Avatar name={fullName(u)} size={22} />
          <b style={{ fontSize: 12 }}>{fullName(u)}</b>
          <RoleBadge role={u.role} />
          {u.position && <span style={{ fontSize: 10, color: '#64748B' }}>{u.position}</span>}
          {ch.length > 0 && <span style={{ fontSize: 10, color: '#64748B' }}>· {ch.length} bezp.{total(u.id) > ch.length ? ` / ${total(u.id)} razem` : ''}</span>}
          {u.deputy && <span style={{ fontSize: 10, color: '#7C3AED' }}>· zastępca: {fullName(u.deputy)}</span>}
          {canApprove(u) && u.role !== 'ADMIN' && <span style={{ fontSize: 9, color: '#059669' }}>✓ {[u.canApproveHours && 'godziny', u.canApproveLeaves && 'urlopy'].filter(Boolean).join(' + ')}</span>}
          {warnings(u).map(([t, c]) => <span key={t} style={{ fontSize: 10, color: c }}>⚠ {t}</span>)}
          {u.role !== 'ADMIN' && (
            <select value={byId[u.supervisorId] ? u.supervisorId : ''} onChange={e => move(u, e.target.value || null)} title="Zmień przełożonego"
              style={{ marginLeft: 'auto', fontSize: 10, padding: '1px 4px', maxWidth: 170 }}>
              <option value="">— bez przełożonego —</option>
              {users.filter(x => x.id !== u.id && !isAncestor(u.id, x.id) && x.role !== 'EVENT').sort(byName).map(x => <option key={x.id} value={x.id}>{fullName(x)}</option>)}
            </select>
          )}
        </div>
        {!isClosed && ch.map(k => renderNode(k, depth + 1))}
      </div>
    );
  };

  const problems = users.filter(u => warnings(u).length).length;

  return (
    <div className="flex flex-col gap-14">
      <div className="page-header">
        <h2 className="page-title">Struktura firmy</h2>
      </div>
      <div className="card">
        <div className="flex gap-8 flex-wrap" style={{ alignItems: 'center', marginBottom: 8 }}>
          <input placeholder="Szukaj osoby…" value={q} onChange={e => setQ(e.target.value)} style={{ maxWidth: 220 }} />
          <button className="btn btn-ghost btn-sm" onClick={() => setClosed({})}>Rozwiń wszystko</button>
          <button className="btn btn-ghost btn-sm" onClick={() => setClosed(Object.fromEntries(Object.keys(kids).map(k => [k, true])))}>Zwiń wszystko</button>
          <span style={{ fontSize: 11, color: problems ? '#B45309' : '#059669', marginLeft: 'auto' }}>{problems ? `⚠ ${problems} do sprawdzenia` : '✓ wszystko poukładane'}</span>
        </div>
        <div style={{ fontSize: 10, color: '#94A3B8', marginBottom: 10 }}>
          Przeciągnij osobę na nowego przełożonego albo wybierz go z listy po prawej. Przełożony zatwierdza godziny i urlopy swoich ludzi,
          jeśli ma do tego uprawnienia (✓). Zastępca przejmuje to pod nieobecność przełożonego.
        </div>
        <div onDragOver={e => { if (drag && drag.supervisorId && drag.role !== 'ADMINISTRATION') e.preventDefault(); }}
          onDrop={e => { if (e.target === e.currentTarget && drag) { const d = drag; setDrag(null); move(d, null); } }}>
          {mainRoots.map(u => renderNode(u, 0))}
        </div>
        {eventLoose.length > 0 && (
          <div style={{ marginTop: 12, borderTop: '1px solid #E2E8F0', paddingTop: 8 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowEvent(v => !v)}>
              {showEvent ? '▾' : '▸'} Eventowi bez przełożonego ({eventLoose.length}) — godziny zatwierdza opiekun eventowych
            </button>
            {(showEvent || q) && eventLoose.filter(u => !q || match(u)).map(u => renderNode(u, 1))}
          </div>
        )}
      </div>
      {ToastComponent}
    </div>
  );
}
